import React, { useCallback, useEffect, useMemo, useState } from "react";
import ExtensionIcon from "@mui/icons-material/Extension";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FiberNewIcon from "@mui/icons-material/FiberNew";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import StarIcon from "@mui/icons-material/Star";
import AppsIcon from "@mui/icons-material/Apps";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Skeleton,
    Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
    JIGSAW_MUSE_GOOGLE_PLAY_URL,
    SHOW_JIGSAW_GOOGLE_PLAY_BADGE,
} from "../../config/nativeAppsVisibility";
import HomeService from "../../services/HomeService";
import Logger from "../../services/Logger";
import UserService from "../../services/UserService";
import UtilService from "../../services/UtilService";
import AppStorePromoPill from "../components/AppStorePromoPill";
import {
    browsePagerIconSx,
    browseTagChipSx,
    browseTextSx,
} from "../../dark-theme/surfaces";
import GooglePlayBadgeLink from "../components/GooglePlayBadgeLink";
import SubscriptionDialog from "../subscription/SubscriptionDialog";
import { usePagesTranslation } from "../locales";
import PuzzleListingAbout from "../components/PuzzleListingAbout";
import BrowseTagScrollRow from "../components/BrowseTagScrollRow";
import BrowseAccessFilter, {
    accessFilterToFreeParam,
} from "../components/BrowseAccessFilter";
import { trackEvent } from "../../services/GoogleAnalytics";
import { TagChipAvatar } from "../puzzles/common/components/TagChipAvatar";
import JigsawService from "../puzzles/jigsaw/JigsawService";
import JigsawBrowseStoryCard from "./JigsawBrowseStoryCard";
import JigsawDailyTreasure from "./JigsawDailyTreasure";

const JIGSAW_MUSE_APP_ICON = `${process.env.PUBLIC_URL || ""}/ad/jigsaw-muse-app-icon-512.png`;

const PAGE_SIZE = 12;
/** Sentinel for the unfiltered chip; API gets no `tags` param. */
const TAG_ALL = "all";
/** Browse pages: English puzzles only (matches crossword / sliding browse). */
const BROWSE_CONTENT_LANGUAGE = "english";

const FEED_BROWSE = "browse";
const FEED_NEW5 = "new5";
const FEED_TOP5 = "top5";
const FEED_EDITORS = "editors";
const FEATURED_LIMIT = 5;

/** Same hero layout as word-search browse; teal (crossword/grid use purple). */
const JIGSAW_HERO_GRADIENT =
    "linear-gradient(125deg, #004d40 0%, #00695c 28%, #00897b 58%, #26a69a 82%, #4db6ac 100%)";

/** Hero-band chips on the teal gradient (readable on dark green). */
const heroFeaturedChipSx = (selected, tone) => {
    const tones = {
        premium: { accent: "#fde68a", selectedBg: "#b45309" },
        new5: { accent: "#7dd3fc", selectedBg: "#0284c7" },
        top5: { accent: "#fbbf24", selectedBg: "#f59e0b" },
        editors: { accent: "#5eead4", selectedBg: "#0d9488" },
        all: { accent: "#fff", selectedBg: "rgba(255,255,255,0.28)" },
    };
    const c = tones[tone] || tones.top5;
    return {
        flexShrink: 0,
        fontWeight: selected ? 700 : 600,
        ...(selected
            ? {
                  bgcolor: c.selectedBg,
                  color: "#fff",
                  borderColor: tone === "all" ? "rgba(255,255,255,0.7)" : c.selectedBg,
                  "& .MuiChip-icon": { color: "#fff" },
                  "&:hover": { bgcolor: c.selectedBg, color: "#fff" },
              }
            : {
                  bgcolor: "rgba(255,255,255,0.12)",
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.45)",
                  "& .MuiChip-icon": { color: c.accent },
                  "&:hover": {
                      bgcolor: "rgba(255,255,255,0.2)",
                      borderColor: c.accent,
                  },
              }),
    };
};

function tagLabelFromRow(row) {
    return typeof row?.tag === "string" ? row.tag : String(row?.tag ?? row?.name ?? "");
}

/** Chip / heading label — e.g. `animal` → `Animal`. Filter still uses the raw tag. */
function formatBrowseTagDisplayLabel(raw) {
    const s = String(raw ?? "").trim();
    if (!s) return "";
    const lower = s.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export default function HomeJigsawBrowsePage() {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();

    const [tags, setTags] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(true);
    const [selectedTag, setSelectedTag] = useState(TAG_ALL);
    const [feed, setFeed] = useState(FEED_NEW5);
    const [accessFilter, setAccessFilter] = useState("all");

    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [listLoading, setListLoading] = useState(true);

    const [userPayPlan, setUserPayPlan] = useState(null);
    const [userPlanResolved, setUserPlanResolved] = useState(false);
    const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);

    useEffect(() => {
        UserService.getUserInfo()
            .then((data) => {
                if (data?.cloudUser) {
                    setUserPayPlan(UtilService.resolveWebPayPlan(data.cloudUser));
                }
            })
            .catch(() => {})
            .finally(() => setUserPlanResolved(true));
    }, []);

    useEffect(() => {
        trackEvent("entered", { name: "HomeJigsawBrowsePage", puzzle_type: "JIGSAW" });
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [page, selectedTag, feed]);

    useEffect(() => {
        if (!userPlanResolved) {
            setTagsLoading(true);
            return undefined;
        }
        let cancel = false;
        setTagsLoading(true);
        HomeService.getJigsawDistinctTags(BROWSE_CONTENT_LANGUAGE)
            .then((list) => {
                if (!cancel) setTags(Array.isArray(list) ? list : []);
            })
            .catch(() => {
                if (!cancel) setTags([]);
            })
            .finally(() => {
                if (!cancel) setTagsLoading(false);
            });
        return () => {
            cancel = true;
        };
    }, [userPlanResolved]);

    useEffect(() => {
        if (!userPlanResolved) return undefined;
        let cancel = false;
        setListLoading(true);

        const load = async () => {
            try {
                if (feed === FEED_NEW5) {
                    const result = await JigsawService.listBrowse({
                        page: 0,
                        pageSize: FEATURED_LIMIT,
                        language: BROWSE_CONTENT_LANGUAGE,
                        sort: "publishedAt:desc",
                        excludeCompleted: false,
                        excludePlayed: false,
                    });
                    if (cancel) return;
                    setRows(Array.isArray(result.content) ? result.content.slice(0, FEATURED_LIMIT) : []);
                    setTotalPages(0);
                    return;
                }
                if (feed === FEED_TOP5) {
                    const result = await JigsawService.listPopular({
                        language: BROWSE_CONTENT_LANGUAGE,
                        limit: FEATURED_LIMIT,
                    });
                    if (cancel) return;
                    setRows(Array.isArray(result.content) ? result.content : []);
                    setTotalPages(0);
                    return;
                }
                if (feed === FEED_EDITORS) {
                    const result = await JigsawService.listEditorsPicks({
                        language: BROWSE_CONTENT_LANGUAGE,
                        limit: FEATURED_LIMIT,
                    });
                    if (cancel) return;
                    setRows(Array.isArray(result.content) ? result.content : []);
                    setTotalPages(0);
                    return;
                }

                const tagFilter = selectedTag === TAG_ALL ? [] : [selectedTag];
                const result = await HomeService.getJigsawPage({
                    page,
                    pageSize: PAGE_SIZE,
                    excludeCompleted: true,
                    excludePlayed: true,
                    sort: "publishedAt:desc",
                    language: BROWSE_CONTENT_LANGUAGE,
                    tags: tagFilter,
                    free: accessFilterToFreeParam(accessFilter),
                });
                if (cancel) return;
                const content = Array.isArray(result.content) ? result.content : [];
                const rawTp = result.totalPages;
                const tp =
                    typeof rawTp === "number" && Number.isFinite(rawTp)
                        ? rawTp
                        : typeof rawTp === "string" && rawTp.trim() !== ""
                          ? Number(rawTp)
                          : 0;
                setTotalPages(Number.isFinite(tp) && tp >= 0 ? tp : 0);
                setRows(content);
            } catch (e) {
                Logger.error("HomeJigsawBrowsePage:", e);
                if (cancel) return;
                setRows([]);
                setTotalPages(0);
            } finally {
                if (!cancel) setListLoading(false);
            }
        };

        load();
        return () => {
            cancel = true;
        };
    }, [userPlanResolved, page, selectedTag, feed, accessFilter]);

    const handleSelectTag = useCallback((tag) => {
        setFeed(FEED_BROWSE);
        setSelectedTag(tag);
        setPage(0);
    }, []);

    const handleSelectFeed = useCallback((nextFeed) => {
        setFeed((current) => (current === nextFeed ? FEED_BROWSE : nextFeed));
        setSelectedTag(TAG_ALL);
        setPage(0);
    }, []);

    const handleAccessFilter = useCallback((next) => {
        setAccessFilter(next);
        setPage(0);
    }, []);

    const goPlay = useCallback(
        (storyId) => {
            navigate(`/puzzle/play-jigsaw/${encodeURIComponent(storyId)}`);
        },
        [navigate],
    );

    const isFeaturedFeed = feed === FEED_NEW5 || feed === FEED_TOP5 || feed === FEED_EDITORS;
    const canGoPrev = page > 0 && !listLoading && !isFeaturedFeed;
    const canGoNext = totalPages > 0 && page < totalPages - 1 && !listLoading && !isFeaturedFeed;
    const showPager = !isFeaturedFeed && totalPages > 1;

    const emptyMessage = (() => {
        if (feed === FEED_NEW5) {
            return t("puzzles-browse.empty-jigsaw-new5", "No new puzzles yet.");
        }
        if (feed === FEED_TOP5) {
            return t("puzzles-browse.empty-jigsaw-top5", "No top puzzles yet.");
        }
        if (feed === FEED_EDITORS) {
            return t("puzzles-browse.empty-jigsaw-editors", "No editor's picks yet.");
        }
        return t("puzzles-browse.empty", "No puzzles available yet.");
    })();

    const tagChipRows = useMemo(
        () =>
            tags
                .map((row) => {
                    const lbl = tagLabelFromRow(row);
                    if (!lbl) return null;
                    return { raw: lbl, label: formatBrowseTagDisplayLabel(lbl) };
                })
                .filter(Boolean),
        [tags]
    );

    return (
        <Box sx={{ flexGrow: 1, p: 3 }}>
            <Paper
                elevation={2}
                sx={{
                    mb: 3,
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: 2,
                    background: JIGSAW_HERO_GRADIENT,
                    color: "#fff",
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 8px 32px rgba(0, 105, 92, 0.28)",
                }}
            >
                <ExtensionIcon
                    sx={{
                        position: "absolute",
                        right: 16,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: 120,
                        color: "rgba(255,255,255,0.14)",
                        pointerEvents: "none",
                        display: { xs: "none", md: "block" },
                    }}
                    aria-hidden
                />
                <Typography
                    variant="h4"
                    component="h1"
                    sx={{ fontWeight: 700, mb: 0.5, position: "relative", zIndex: 1 }}
                >
                    {t("puzzles-browse.page-title-jigsaw")}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.95, position: "relative", zIndex: 1 }}>
                    {t("puzzles-browse.page-subtitle-jigsaw")}
                </Typography>
            </Paper>

            <JigsawDailyTreasure />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 2.5 }}>
                        <Chip
                            component={RouterLink}
                            to="/puzzles/jigsaw/packs"
                            icon={<WorkspacePremiumOutlinedIcon />}
                            label={t("puzzles-browse.jigsaw-premium-pack-chip", "Premium pack")}
                            clickable
                            size="small"
                            variant="outlined"
                            sx={{
                                ...heroFeaturedChipSx(false, "premium"),
                                textDecoration: "none",
                            }}
                        />
                        <Chip
                            icon={<FiberNewIcon />}
                            label={t("puzzles-browse.filter-jigsaw-new5", "New 5")}
                            clickable
                            size="small"
                            variant={feed === FEED_NEW5 ? "filled" : "outlined"}
                            aria-pressed={feed === FEED_NEW5}
                            onClick={() => handleSelectFeed(FEED_NEW5)}
                            sx={heroFeaturedChipSx(feed === FEED_NEW5, "new5")}
                        />
                        <Chip
                            icon={<WhatshotIcon />}
                            label={t("puzzles-browse.filter-jigsaw-top5", "Top 5")}
                            clickable
                            size="small"
                            variant={feed === FEED_TOP5 ? "filled" : "outlined"}
                            aria-pressed={feed === FEED_TOP5}
                            onClick={() => handleSelectFeed(FEED_TOP5)}
                            sx={heroFeaturedChipSx(feed === FEED_TOP5, "top5")}
                        />
                        <Chip
                            icon={<StarIcon />}
                            label={t("puzzles-browse.filter-jigsaw-editors", "Editor's picks")}
                            clickable
                            size="small"
                            variant={feed === FEED_EDITORS ? "filled" : "outlined"}
                            aria-pressed={feed === FEED_EDITORS}
                            onClick={() => handleSelectFeed(FEED_EDITORS)}
                            sx={heroFeaturedChipSx(feed === FEED_EDITORS, "editors")}
                        />
                        <Chip
                            icon={<AppsIcon />}
                            label={t("puzzles-browse.tag-all", "All")}
                            clickable
                            size="small"
                            variant={feed === FEED_BROWSE ? "filled" : "outlined"}
                            aria-pressed={feed === FEED_BROWSE}
                            onClick={() => handleSelectTag(TAG_ALL)}
                            sx={heroFeaturedChipSx(feed === FEED_BROWSE, "all")}
                        />
            </Box>

            {isFeaturedFeed ? null : (
                <BrowseAccessFilter value={accessFilter} onChange={handleAccessFilter} t={t} />
            )}

            {isFeaturedFeed ? (
                <Box
                    sx={{
                        mb: 2.5,
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor:
                            feed === FEED_NEW5
                                ? "rgba(2, 132, 199, 0.35)"
                                : feed === FEED_TOP5
                                  ? "rgba(245, 158, 11, 0.35)"
                                  : "rgba(13, 148, 136, 0.35)",
                        background:
                            feed === FEED_NEW5
                                ? "linear-gradient(120deg, rgba(2, 132, 199, 0.14) 0%, rgba(2, 132, 199, 0.04) 55%, transparent 100%)"
                                : feed === FEED_TOP5
                                  ? "linear-gradient(120deg, rgba(245, 158, 11, 0.14) 0%, rgba(245, 158, 11, 0.04) 55%, transparent 100%)"
                                  : "linear-gradient(120deg, rgba(13, 148, 136, 0.14) 0%, rgba(13, 148, 136, 0.04) 55%, transparent 100%)",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.75 }}>
                        {feed === FEED_NEW5 ? (
                            <FiberNewIcon sx={{ color: "#0284c7", fontSize: 32 }} />
                        ) : feed === FEED_TOP5 ? (
                            <WhatshotIcon sx={{ color: "#f59e0b", fontSize: 32 }} />
                        ) : (
                            <StarIcon sx={{ color: "#0d9488", fontSize: 32 }} />
                        )}
                        <Typography
                            variant="h4"
                            component="h2"
                            sx={{
                                fontWeight: 800,
                                letterSpacing: "-0.02em",
                                lineHeight: 1.15,
                                color:
                                    feed === FEED_NEW5 ? "#0369a1" : feed === FEED_TOP5 ? "#b45309" : "#0f766e",
                                ...browseTextSx,
                            }}
                        >
                            {feed === FEED_NEW5
                                ? t("puzzles-browse.title-jigsaw-new5", "New 5")
                                : feed === FEED_TOP5
                                  ? t("puzzles-browse.title-jigsaw-top5", "Most played in a week")
                                  : t("puzzles-browse.title-jigsaw-editors", "Editor's picks")}
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: "text.secondary",
                            maxWidth: 720,
                            lineHeight: 1.55,
                            ...browseTextSx,
                        }}
                    >
                        {feed === FEED_NEW5
                            ? t(
                                  "puzzles-browse.blurb-jigsaw-new5",
                                  "The five puzzles published most recently. Start with the latest pictures.",
                              )
                            : feed === FEED_TOP5
                              ? t(
                                    "puzzles-browse.blurb-jigsaw-top5",
                                    "The five puzzles played most often in the last 7 days.",
                                )
                              : t(
                                    "puzzles-browse.blurb-jigsaw-editors",
                                    "Hand-picked by our team for beautiful art, satisfying cuts, and a memorable play. Fresh picks land here first.",
                                )}
                    </Typography>
                </Box>
            ) : (
                <BrowseTagScrollRow
                    ariaLabel={t("puzzles-browse.filters-tags", "Tags")}
                    scrollLeftLabel={t("puzzle.tags.scroll-left", "Scroll left")}
                    scrollRightLabel={t("puzzle.tags.scroll-right", "Scroll right")}
                >
                    {tagsLoading ? (
                        <>
                            <Skeleton variant="rounded" width={64} height={32} sx={{ borderRadius: 16, flexShrink: 0 }} />
                            <Skeleton variant="rounded" width={88} height={32} sx={{ borderRadius: 16, flexShrink: 0 }} />
                            <Skeleton variant="rounded" width={96} height={32} sx={{ borderRadius: 16, flexShrink: 0 }} />
                            <Skeleton variant="rounded" width={72} height={32} sx={{ borderRadius: 16, flexShrink: 0 }} />
                        </>
                    ) : (
                        <>
                            <Chip
                                label={t("puzzles-browse.tag-all", "All")}
                                clickable
                                onClick={() => handleSelectTag(TAG_ALL)}
                                color={selectedTag === TAG_ALL ? "primary" : "default"}
                                variant={selectedTag === TAG_ALL ? "filled" : "outlined"}
                                sx={{
                                    ...browseTagChipSx(selectedTag === TAG_ALL),
                                    flexShrink: 0,
                                }}
                            />
                            {tagChipRows.map(({ raw, label }) => {
                                const selected = selectedTag === raw;
                                return (
                                    <Chip
                                        key={raw}
                                        avatar={<TagChipAvatar tag={raw} size={18} />}
                                        label={label}
                                        clickable
                                        onClick={() => handleSelectTag(raw)}
                                        color={selected ? "primary" : "default"}
                                        variant={selected ? "filled" : "outlined"}
                                        sx={{ ...browseTagChipSx(selected), flexShrink: 0 }}
                                    />
                                );
                            })}
                        </>
                    )}
                </BrowseTagScrollRow>
            )}

            <Box sx={{ width: "100%", maxWidth: 1600, mx: "auto" }}>
                {listLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : rows.length === 0 ? (
                    <Typography sx={{ py: 4, textAlign: "center", ...browseTextSx }}>
                        {emptyMessage}
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, minmax(0, 1fr))",
                                md: "repeat(3, minmax(0, 1fr))",
                                lg: "repeat(5, minmax(0, 1fr))",
                            },
                            gap: { xs: 2, md: 2.5 },
                            justifyContent: "center",
                            alignItems: "stretch",
                        }}
                    >
                        {rows.map((group, idx) => {
                            const story = group.story;
                            const storyId = story?.id;
                            const templates = group.templates ?? [];

                            return (
                                <JigsawBrowseStoryCard
                                    key={storyId || idx}
                                    story={story}
                                    storyJigsaw={group.storyJigsaw}
                                    tags={group.puzzleTemplate?.tags}
                                    templatesCount={templates.length}
                                    playCount={group.playCount}
                                    showThumbsUp={feed === FEED_TOP5}
                                    userPayPlan={userPayPlan}
                                    onClick={goPlay}
                                    onUpgrade={() => setSubscriptionModalOpen(true)}
                                />
                            );
                        })}
                    </Box>
                )}

                {showPager ? (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                            mt: 3,
                            mb: 1,
                        }}
                    >
                        <IconButton
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={!canGoPrev}
                            aria-label={t("puzzles-browse.page-previous", "Previous")}
                            size="large"
                            sx={browsePagerIconSx}
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                        <Typography
                            variant="body2"
                            sx={{ minWidth: 72, textAlign: "center", ...browseTextSx }}
                        >
                            {page + 1} / {totalPages}
                        </Typography>
                        <IconButton
                            onClick={() => setPage((p) => p + 1)}
                            disabled={!canGoNext}
                            aria-label={t("puzzles-browse.page-next", "Next")}
                            size="large"
                            sx={browsePagerIconSx}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </Box>
                ) : null}

                {SHOW_JIGSAW_GOOGLE_PLAY_BADGE ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            mt: showPager ? 2 : 3,
                            mb: 1,
                        }}
                    >
                        <AppStorePromoPill
                            iconSrc={JIGSAW_MUSE_APP_ICON}
                            appName="Jigsaw Muse"
                            tagline={t(
                                "marketing.jigsaw-muse-promo",
                                "Classic photo jigsaws with Color Baskets.",
                            )}
                        >
                            <GooglePlayBadgeLink
                                height={36}
                                href={JIGSAW_MUSE_GOOGLE_PLAY_URL}
                                ariaLabel={t(
                                    "landing.jigsaw-google-play-badge-aria",
                                    "Get Jigsaw Muse on Google Play (opens in a new tab)",
                                )}
                            />
                        </AppStorePromoPill>
                    </Box>
                ) : null}

                <PuzzleListingAbout puzzleKind="JIGSAW" />

                <SubscriptionDialog
                    open={subscriptionModalOpen}
                    onClose={() => setSubscriptionModalOpen(false)}
                    onSubscribe={() => {
                        setSubscriptionModalOpen(false);
                        window.location.reload();
                    }}
                    triggerReason="puzzle"
                    userSubscription={userPayPlan || "free"}
                />
            </Box>
        </Box>
    );
}
